import React, { useContext, useState } from 'react'
import Address from './Address';
import DropDown from './DropDown';
import Options from './Options';
import { AzanCallOptions } from '../data/AzanCallOptions';
import { DeviceModes } from '../data/DeviceModes';
import { FajrAzans, Azans } from '../data/Audios';
import { CalculationMethods, AsrCalculationMethods } from '../data/CalculationMethods';
import { AppContext } from '../AppContext';
import { FontAwesome } from '../data/FontAwesome';
import { format12 } from '../scripts/SmartAzanClock';
import { Languages } from '../data/I18n';
import { DisplayThemes } from '../data/DisplayThemes';
import DeviceInfo from './DeviceInfo';

export default function Settings() {

    const { vakits, arcVakits, calculationSettings, locationSettings, deviceSettings, azanSettings,
        offsetSettings, updateSettings, updateOffset, previewAudio, oneThirdTime, twoThirdTime, midnightTime, t } = useContext(AppContext)
    const [activePage, setActivePage] = useState('location')

    const updateDeviceSetting = (key, value) => {
        updateSettings({ deviceSettings: { ...deviceSettings, [key]: value } });
    }

    const CalculationMethodValues = [];
    Object.keys(CalculationMethods).forEach(k => {
        CalculationMethodValues.push({ id: k, name: CalculationMethods[k].name });
    })

    const isRamadan = new Intl.DateTimeFormat('en-u-ca-islamic', { month: 'numeric' }).format(new Date()) === '9';
    const azanEnabled = deviceSettings.azanCallsEnabled === 'Y';
    const YesNoOptions = AzanCallOptions.map(o => ({ ...o, name: t(o.id === 'Y' ? 'common.yes' : 'common.no') }));
    const LocalDeviceModes = DeviceModes.map(o => ({
        ...o,
        name: t(o.id === 'A' ? 'options.auto' : (o.id === 'D' ? 'options.dim' : 'options.normal'))
    }));
    const LocalAsrCalculationMethods = AsrCalculationMethods.map(o => ({
        ...o,
        name: t(o.id === 'H' ? 'options.hanafi' : 'options.standard')
    }));
    const LocalDisplayThemes = DisplayThemes.map(o => ({ ...o, name: t(o.nameKey) }));
    const azanSettingsHTML = [];
    const Vakits = ['Fajr', 'Dhuhr', 'Asr', 'Maghrib', 'Isha'];
    Vakits.forEach((item) => {

        let cVakit = item.toLowerCase();
        let azanValue = azanSettings[cVakit];
        let offsetValue = offsetSettings[cVakit];
        let values = (item === "Fajr") ? FajrAzans : Azans;
        let vTime = vakits.find(v => v.name === item).displayTime;

        azanSettingsHTML.push(

            <div key={item} className="settings-azan-row">

                <div className='settings-azan-heading'>
                    <div><span>{t('prayers.' + item)} {t('settings.azan')}</span><small>{t('common.at')} {vTime}</small></div>
                    <div><small>{t('settings.minuteOffset')}</small></div>
                </div>

                <div className='settings-azan-controls'>

                    <div className='settings-azan-select'>
                        <DropDown name={'azanSettings.' + cVakit} selectedValue={azanValue} values={values} disabled={!azanEnabled} />
                    </div>
                    <div className='settings-preview'>
                        <button onClick={() => { previewAudio(azanValue * 1); document.activeElement.blur(); }}
                            type='button'
                            disabled={!azanEnabled}
                            className='settings-play-btn'>{FontAwesome.Play}<span>{t('media.preview')}</span></button>
                    </div>
                    <div className='settings-offset'>
                        <div className='settings-stepper'>
                            <button type='button' aria-label={t('media.decreaseOffset')} onClick={() => { updateOffset(cVakit, '-'); document.activeElement.blur(); }} className='settings-stepper-btn'>{FontAwesome.Minus}</button>
                            <button type='button' aria-label={t('media.resetOffset')} onClick={() => { updateOffset(cVakit, '0'); document.activeElement.blur(); }} className={'settings-stepper-btn settings-stepper-value ' + ((offsetValue === 0) ? '' : 'is-adjusted')}>{offsetValue}</button>
                            <button type='button' aria-label={t('media.increaseOffset')} onClick={() => { updateOffset(cVakit, '+'); document.activeElement.blur(); }} className='settings-stepper-btn'>{FontAwesome.Plus}</button>
                        </div>
                    </div>
                </div>

            </div >

        )
    })

    const settingsPages = [
        { id: 'location', label: t('settings.tabLocation'), icon: FontAwesome.Search },
        { id: 'display', label: t('settings.tabDisplay'), icon: FontAwesome.Gear },
        { id: 'calculation', label: t('settings.tabCalculation'), icon: FontAwesome.Check },
        { id: 'azan', label: t('settings.tabAzan'), icon: FontAwesome.Bell },
        { id: 'times', label: t('settings.tabTimes'), icon: FontAwesome.Book },
        { id: 'device', label: t('settings.tabDevice'), icon: FontAwesome.Info }
    ];

    return (
        <div className='settings-panel'>

            {isRamadan && <div className='alert alert-warning p-2 mb-2' style={{ fontSize: '0.85rem' }}>
                <strong>{t('settings.ramadanTipTitle')}</strong> {t('settings.ramadanTipBody')}
            </div>}

            <div className='settings-page-tabs'>
                {settingsPages.map(page => (
                    <button
                        className={'settings-page-tab ' + (activePage === page.id ? 'active' : '')}
                        key={page.id}
                        type='button'
                        onClick={() => setActivePage(page.id)}>
                        <span>{page.icon}</span>
                        <strong>{page.label}</strong>
                    </button>
                ))}
            </div>

            {activePage === 'location' ? <section className='settings-section'>
                <div className='settings-section-title'>{t('settings.location')}</div>
                <Address value={locationSettings.address} />
            </section> : null}

            {activePage === 'display' ? <section className='settings-section'>
                <div className='settings-section-title settings-title-with-copy'>
                    <span>{t('layouts.label')}</span>
                    <small>{t('settings.displayIntro')}</small>
                </div>
                <div className='settings-grid settings-display-grid'>
                    <div className="settings-field">
                        <span>{t('common.language')}</span>
                        <Options name="deviceSettings.language" selectedValue={deviceSettings.language || 'en'} values={Languages} />
                    </div>

                    <div className="settings-field settings-field-wide">
                        <span>{t('layouts.chooseLayout')}</span>
                        <div className='settings-layout-picker'>
                            <button
                                type='button'
                                className={'settings-layout-card ' + ((deviceSettings.layout || 'clock') === 'clock' ? 'active' : '')}
                                aria-pressed={(deviceSettings.layout || 'clock') === 'clock'}
                                onClick={() => updateDeviceSetting('layout', 'clock')}>
                                <span className='settings-layout-preview settings-layout-preview-clock' aria-hidden='true'>
                                    <i></i><i></i><i></i><i></i><i></i><i></i><em>12:18</em>
                                </span>
                                <span className='settings-layout-copy'>
                                    <strong>{t('layouts.clock')}</strong>
                                    <small>{t('layouts.clockDescription')}</small>
                                </span>
                                <span className='settings-layout-check'>{FontAwesome.Check}</span>
                            </button>

                            <button
                                type='button'
                                className={'settings-layout-card ' + (deviceSettings.layout === 'dashboard' ? 'active' : '')}
                                aria-pressed={deviceSettings.layout === 'dashboard'}
                                onClick={() => updateDeviceSetting('layout', 'dashboard')}>
                                <span className='settings-layout-preview settings-layout-preview-dashboard' aria-hidden='true'>
                                    <i></i><i></i><i></i><b></b>
                                </span>
                                <span className='settings-layout-copy'>
                                    <strong>{t('layouts.dashboard')}</strong>
                                    <small>{t('layouts.dashboardDescription')}</small>
                                </span>
                                <span className='settings-layout-check'>{FontAwesome.Check}</span>
                            </button>
                        </div>
                    </div>

                    <div className={'settings-screensaver-card settings-field-wide ' + (deviceSettings.screenSaver === 'Y' ? 'is-enabled' : '')}>
                        <span className='settings-screensaver-visual' aria-hidden='true'>
                            <i></i><b></b>
                        </span>
                        <span className='settings-screensaver-copy'>
                            <strong>{t('settings.screenSaverMode')}</strong>
                            <small>{t('settings.screenSaverDescription')}</small>
                        </span>
                        <button
                            type='button'
                            className='settings-switch'
                            role='switch'
                            aria-checked={deviceSettings.screenSaver === 'Y'}
                            onClick={() => updateDeviceSetting('screenSaver', deviceSettings.screenSaver === 'Y' ? 'N' : 'Y')}>
                            <span></span>
                            <b>{deviceSettings.screenSaver === 'Y' ? t('common.yes') : t('common.no')}</b>
                        </button>
                    </div>

                    <div className="settings-field">
                        <span>{t('settings.displayMode')}</span>
                        <Options name="deviceSettings.mode" selectedValue={deviceSettings.mode} values={LocalDeviceModes} />
                    </div>

                    <div className="settings-field">
                        <span>{t('themes.label')}</span>
                        <Options name="deviceSettings.theme" selectedValue={deviceSettings.theme || 'classic'} values={LocalDisplayThemes} />
                    </div>

                    <div className="settings-field">
                        <span>{t('settings.zoomedInMode')}</span>
                        <Options name="deviceSettings.zoomedIn" selectedValue={deviceSettings.zoomedIn} values={YesNoOptions} />
                    </div>
                </div>
            </section> : null}

            {activePage === 'calculation' ? <section className='settings-section'>
                <div className='settings-section-title'>{t('settings.calculationMethod')}</div>
                <div className='settings-grid'>
                    <div className="settings-field">
                        <span>{t('settings.calculationMethod')}</span>
                        <DropDown name="calculationSettings.method" selectedValue={calculationSettings.method} values={CalculationMethodValues} />
                    </div>

                    <div className="settings-field">
                        <span>{t('settings.asrCalculationMethod')}</span>
                        <Options name="calculationSettings.asrMethod" selectedValue={calculationSettings.asrMethod} values={LocalAsrCalculationMethods} />
                    </div>
                </div>
            </section> : null}

            {activePage === 'azan' ? <section className='settings-section'>
                <div className='settings-section-title'>{t('settings.enableAzanCalls')}</div>
                <div className="settings-field">
                    <span>{t('settings.enableAzanCalls')}</span>
                    <Options name="deviceSettings.azanCallsEnabled" selectedValue={deviceSettings.azanCallsEnabled} values={YesNoOptions} />
                </div>

                <div className='settings-azan-list'>{azanSettingsHTML}</div>
            </section> : null}

            {activePage === 'times' ? <section className='settings-section'>
                <div className='settings-section-title'>{t('dashboard.night')}</div>
                <div className='settings-time-chips'>
                    <div>{t('settings.imsak')} <strong>{format12(arcVakits.find(f => f.name === 'Imsak').time)}</strong></div>
                    <div>{t('settings.duha')} <strong>{format12(arcVakits.find(f => f.name === 'Duha').time)} - {format12(arcVakits.find(f => f.name === 'Duhaend').time)}</strong></div>
                    <div>1/3 <strong>{format12(oneThirdTime)}</strong></div>
                    <div>{t('settings.midnight')} <strong>{format12(midnightTime)}</strong></div>
                    <div>2/3 <strong>{format12(twoThirdTime)}</strong></div>
                </div>
            </section> : null}

            {activePage === 'device' ? <section className='settings-section'>
                <DeviceInfo />
            </section> : null}

        </div >
    )
}
